import React, { useEffect, useRef, useState } from "react";

interface Props {
  value: string;
  /** Başlıkta görünen ad (tooltip için) */
  label?: string;
  className?: string;
}

/**
 * Kimlik kopyalama butonu — tablo hücresinde GUID'in kendisini göstermek yerine
 * tek bir küçük ikon. Tıklayınca panoya kopyalar, 1,5 sn "✓" gösterir.
 * Tam kimlik tooltip'te (title) görünür.
 */
const CopyIdButton: React.FC<Props> = ({ value, label = "Order ID", className }) => {
  const [state, setState] = useState<"idle" | "done" | "fail">("idle");
  const timer = useRef<number | null>(null);

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  const flash = (s: "done" | "fail") => {
    setState(s);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState("idle"), 1500);
  };

  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      flash("done");
    } catch {
      // Güvenli olmayan bağlam / eski tarayıcı: gizli textarea ile kopyala
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        flash(document.execCommand("copy") ? "done" : "fail");
      } catch {
        flash("fail");
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  return (
    <button
      type="button"
      className={`copy-id-btn ${state !== "idle" ? `is-${state}` : ""} ${className || ""}`}
      onClick={copy}
      title={state === "done" ? "Kopyalandı" : `${label} kopyala: ${value}`}
      aria-label={`${label} kopyala`}
    >
      {state === "done" ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : state === "fail" ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
    </button>
  );
};

export default CopyIdButton;
